import type { GraphqlJob } from "@noiman/shared-graphql";

export const JOB_FACET_TYPES = [
  "role",
  "skill",
  "location",
  "condition",
] as const;

export type JobFacetType = (typeof JOB_FACET_TYPES)[number];

type FacetJob = Pick<
  GraphqlJob,
  | "category"
  | "company"
  | "id"
  | "lastVerifiedAt"
  | "lifecycleStatus"
  | "location"
  | "skills"
  | "sourceUrlHealthy"
  | "specialTags"
  | "type"
  | "workStyle"
>;

export interface JobFacetAggregate {
  type: JobFacetType;
  slug: string;
  label: string;
  jobs: FacetJob[];
  activeJobCount: number;
  distinctCompanyCount: number;
  aggregateUpdatedAt: string | null;
}

export interface JobFacetIndexGate {
  indexable: boolean;
  reasons: string[];
}

/**
 * Stable, URL-safe facet key. It intentionally does not invent English
 * translations: editorial aliases belong in reviewed landing definitions.
 */
export function toJobFacetSlug(value: string): string {
  return value
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/\+/g, "-plus")
    .replace(/#/g, "-sharp")
    .replace(/[^\p{Letter}\p{Number}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function facetValues(job: FacetJob, type: JobFacetType): string[] {
  switch (type) {
    case "role":
      return [job.category];
    case "skill":
      return job.skills;
    case "location":
      return [job.location];
    case "condition":
      return [job.type, ...job.workStyle, ...job.specialTags];
  }
}

function newestVerification(jobs: readonly FacetJob[]): string | null {
  let newest = Number.NEGATIVE_INFINITY;
  let value: string | null = null;
  for (const job of jobs) {
    if (!job.lastVerifiedAt) continue;
    const timestamp = new Date(job.lastVerifiedAt).getTime();
    if (Number.isFinite(timestamp) && timestamp > newest) {
      newest = timestamp;
      value = new Date(timestamp).toISOString();
    }
  }
  return value;
}

export function buildJobFacetAggregates(
  jobs: readonly FacetJob[],
  type: JobFacetType,
): JobFacetAggregate[] {
  const activeJobs = jobs.filter(
    (job) =>
      job.lifecycleStatus === "published" && job.sourceUrlHealthy === true,
  );
  const groups = new Map<string, { label: string; jobs: FacetJob[] }>();

  for (const job of activeJobs) {
    const values = new Map(
      facetValues(job, type)
        .map((label) => [toJobFacetSlug(label), label.trim()] as const)
        .filter(([slug, label]) => slug.length > 0 && label.length > 0),
    );
    for (const [slug, label] of values) {
      const group = groups.get(slug) ?? { label, jobs: [] };
      group.jobs.push(job);
      groups.set(slug, group);
    }
  }

  return [...groups.entries()]
    .map(([slug, group]) => ({
      type,
      slug,
      label: group.label,
      jobs: group.jobs,
      activeJobCount: group.jobs.length,
      distinctCompanyCount: new Set(group.jobs.map((job) => job.company.id))
        .size,
      aggregateUpdatedAt: newestVerification(group.jobs),
    }))
    .sort(
      (left, right) =>
        right.activeJobCount - left.activeJobCount ||
        left.slug.localeCompare(right.slug, "ja"),
    );
}

export function findJobFacetAggregate(
  jobs: readonly FacetJob[],
  type: JobFacetType,
  slug: string,
): JobFacetAggregate | null {
  const normalized = toJobFacetSlug(slug);
  return (
    buildJobFacetAggregates(jobs, type).find(
      (aggregate) => aggregate.slug === normalized,
    ) ?? null
  );
}

/** Phase 2 non-crawl SEO hard gate from section 13.3 of the Jobs spec. */
export function evaluateJobFacetIndexGate(
  aggregate: Pick<
    JobFacetAggregate,
    "activeJobCount" | "aggregateUpdatedAt" | "distinctCompanyCount"
  >,
  options: {
    featureEnabled: boolean;
    hasHumanReviewedIntro: boolean;
    hasUniqueInsight: boolean;
    canonicalIntentUnique: boolean;
    now?: Date;
  },
): JobFacetIndexGate {
  const reasons: string[] = [];
  if (!options.featureEnabled) reasons.push("feature_disabled");
  if (aggregate.activeJobCount < 5) reasons.push("too_few_active_jobs");
  if (aggregate.distinctCompanyCount < 3) reasons.push("too_few_companies");
  if (!options.hasHumanReviewedIntro) reasons.push("intro_not_reviewed");
  if (!options.hasUniqueInsight) reasons.push("unique_insight_missing");
  if (!options.canonicalIntentUnique)
    reasons.push("canonical_intent_not_unique");

  const updatedAt = aggregate.aggregateUpdatedAt
    ? new Date(aggregate.aggregateUpdatedAt)
    : null;
  const now = options.now ?? new Date();
  if (
    !updatedAt ||
    Number.isNaN(updatedAt.getTime()) ||
    updatedAt.getTime() > now.getTime() ||
    now.getTime() - updatedAt.getTime() > 7 * 86_400_000
  ) {
    reasons.push("aggregate_stale");
  }

  return { indexable: reasons.length === 0, reasons };
}
